import { useState, useRef, useCallback, useEffect, useMemo } from 'react';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { usePageSpaceKey } from '@confluence/page-context';

import {
	FolderOverviewDualStreamFirstPageQuery,
	FolderOverviewDualStreamPaginateQuery,
} from './DualStreamQueries.ts';
import type {
	DualStreamQueriesFirstPageQuery$data,
	DualStreamQueriesFirstPageQuery$variables,
} from './__generated__/DualStreamQueriesFirstPageQuery.graphql.ts';
import type {
	DualStreamQueriesPaginateQuery$data,
	DualStreamQueriesPaginateQuery$variables,
} from './__generated__/DualStreamQueriesPaginateQuery.graphql.ts';

/** Number of items to fetch per API call (overfetch into buffer). */
export const FETCH_SIZE = 100;
/** Number of items to reveal per scroll page. */
const PAGE_SIZE = 40;
/** When the un-displayed buffer falls below this threshold, prefetch the next page. */
const REFILL_THRESHOLD = 20;

type ContentTreeNode = {
	readonly id: string;
	readonly title: string | null;
	readonly status: string | null;
	readonly type: string | null;
	readonly subType: string | null;
	readonly emojiTitlePublished: string | null;
	readonly links: { readonly webui: string | null } | null;
	readonly properties: {
		readonly nodes: ReadonlyArray<{
			readonly id: string;
			readonly key: string | null;
			readonly value: string | null;
		} | null> | null;
	} | null;
};

type StreamState = {
	nodes: ContentTreeNode[];
	cursor: string | null;
	hasNextPage: boolean;
	lastPageCount: number;
	exhausted: boolean;
};

const EMPTY_STREAM: StreamState = {
	nodes: [],
	cursor: null,
	hasNextPage: false,
	lastPageCount: -1, // -1 = never fetched
	exhausted: false,
};

const getStreamSignature = (
	stream:
		| DualStreamQueriesFirstPageQuery$data['folders']
		| DualStreamQueriesFirstPageQuery$data['content'],
): string => {
	const ids = stream?.nodes?.map((node) => node?.id ?? '').join(',') ?? '';
	const endCursor = stream?.pageInfo?.endCursor ?? '';
	const hasNextPage = String(Boolean(stream?.pageInfo?.hasNextPage));
	return `${ids}|${endCursor}|${hasNextPage}`;
};

const getInitialDataSignature = (data: DualStreamQueriesFirstPageQuery$data): string =>
	`${getStreamSignature(data.folders)}::${getStreamSignature(data.content)}`;

/**
 * Builds a {@link StreamState} from a stream's query data. Shared between the
 * initial-load merge effect and the synchronous render-time derivation so both
 * produce identical stream buffers.
 */
const buildStreamState = (
	stream:
		| DualStreamQueriesFirstPageQuery$data['folders']
		| DualStreamQueriesFirstPageQuery$data['content'],
): StreamState => {
	const nodes: ContentTreeNode[] = [];
	for (const n of stream?.nodes ?? []) {
		if (n?.id) {
			nodes.push(n as ContentTreeNode);
		}
	}
	const pageInfo = stream?.pageInfo;
	return {
		nodes,
		cursor: pageInfo?.endCursor ?? null,
		hasNextPage: pageInfo?.hasNextPage ?? false,
		lastPageCount: nodes.length,
		exhausted: nodes.length === 0 || !(pageInfo?.hasNextPage ?? false),
	};
};

type DualStreamPaginationResult = {
	/** The combined visible child nodes (folders first, then content). */
	visibleNodes: ContentTreeNode[];
	/** Whether the initial load is still in progress. */
	loading: boolean;
	/** Whether a pagination fetch is in progress. */
	isFetchingMore: boolean;
	/** The ptpage data (folder metadata). */
	ptpage: DualStreamQueriesFirstPageQuery$data['ptpage'] | null;
	/** The singleContent data (permissions). */
	singleContent: DualStreamQueriesFirstPageQuery$data['singleContent'] | null;
	/** Any error from the initial query. */
	error: Error | null;
	/** Any error from a pagination query (non-fatal — existing items remain visible). */
	paginateError: Error | null;
	/** Load the next page of visible items (called by IntersectionObserver). */
	loadNextPage: () => void;
	/** Reset pagination (e.g. on content update). */
	resetPagination: () => void;
	/** All visible child IDs as an array (for enrichment query). */
	childIds: string[];
	/** All folder node IDs for refetch subscription. */
	childIdsSet: Set<string>;
};

type UseDualStreamPaginationOptions = {
	readonly includeDevDocsSyncStatus?: boolean;
};

/**
 * Manages dual-stream (folders + content) pagination with client-side buffering.
 *
 * On initial load, overfetches 100 folders and 100 content items in a single
 * GraphQL request. Displays 40 items at a time, exhausting all folders before
 * showing any content. Each stream is independently paginated when its buffer
 * runs low.
 */
export const useDualStreamPagination = (
	contentId: string,
	{ includeDevDocsSyncStatus = false }: UseDualStreamPaginationOptions = {},
): DualStreamPaginationResult => {
	const { cloudId } = useSessionData();
	const [currentSpaceKey] = usePageSpaceKey();
	const spaceKey = currentSpaceKey || '';

	// --- Stream buffers ---
	const [folderStream, setFolderStream] = useState<StreamState>(EMPTY_STREAM);
	const [contentStream, setContentStream] = useState<StreamState>(EMPTY_STREAM);

	// --- Display state ---
	const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
	const visibleCountRef = useRef(visibleCount);
	visibleCountRef.current = visibleCount;
	const [isFetchingMore, setIsFetchingMore] = useState(false);
	// Captured pagination error — persists after the paginate query is skipped
	// so the caller can log it for observability.
	const [lastPaginateError, setLastPaginateError] = useState<Error | null>(null);

	// --- Merge initial load data into stream buffers ---
	const lastInitialDataRef = useRef<unknown>(null);
	const lastInitialDataSignatureRef = useRef<string | null>(null);
	const skipInitialDataRef = useRef<unknown>(null);

	// Reset stream state when navigating to a different folder.
	const prevContentIdRef = useRef(contentId);
	useEffect(() => {
		if (prevContentIdRef.current !== contentId) {
			prevContentIdRef.current = contentId;
			lastInitialDataRef.current = null;
			lastInitialDataSignatureRef.current = null;
			skipInitialDataRef.current = null;
			setFolderStream(EMPTY_STREAM);
			setContentStream(EMPTY_STREAM);
			setVisibleCount(PAGE_SIZE);
			setIsFetchingMore(false);
			setLastPaginateError(null);
		}
	}, [contentId]);

	// --- Initial load query ---
	const { data, error, loading, refetch } = useQueryInterop<
		DualStreamQueriesFirstPageQuery$data,
		DualStreamQueriesFirstPageQuery$variables
	>(FolderOverviewDualStreamFirstPageQuery, {
		variables: {
			cloudId: cloudId || '',
			parentId: contentId,
			spaceKey,
			contentId,
			first: FETCH_SIZE,
			includeDevDocsSyncStatus,
		},
		fetchPolicy: 'network-only',
	});

	useEffect(() => {
		if (loading || !data) {
			return;
		}

		if (skipInitialDataRef.current === data) {
			return;
		}

		if (skipInitialDataRef.current) {
			skipInitialDataRef.current = null;
			lastInitialDataSignatureRef.current = null;
		}

		const dataSignature = getInitialDataSignature(data);
		if (lastInitialDataSignatureRef.current === dataSignature) {
			return;
		}

		lastInitialDataRef.current = data;
		lastInitialDataSignatureRef.current = dataSignature;

		setFolderStream(buildStreamState(data.folders));
		setContentStream(buildStreamState(data.content));
	}, [data, loading, contentId]);

	// Synchronously derive stream buffers directly from `data` for the current
	// render pass. `useEffect` never runs during SSR (and hasn't fired yet on the
	// very first client render), so without this the buffers stay at EMPTY_STREAM
	// and `visibleNodes` is empty — making FolderOverviewDualStream render nothing
	// on the server even when the query was preloaded into a warm store. Mirrors
	// the legacy path's synchronous seed (see FolderOverview.tsx `effectiveChildNodes`).
	// Note: intentionally NOT gated on `loading`. `useQueryInterop` reports
	// loading=true alongside data when serving a warm store with a network-only
	// policy (and on SSR `loading` stays true because the resolving effect never
	// runs), so gating on `!loading` would suppress SSR content. The legacy path
	// makes the same choice.
	const dataDerivedStreams = useMemo(() => {
		if (!data) {
			return null;
		}
		return {
			folder: buildStreamState(data.folders),
			content: buildStreamState(data.content),
		};
	}, [data]);

	// Use the data-derived streams while the state buffers are still unpopulated
	// (lastPageCount === -1); once the merge effect has run on the client, the
	// state buffers take over so pagination continues to accumulate correctly.
	//
	// Honour `skipInitialDataRef`: `resetPagination` empties the buffers AND marks
	// the current `data` as skipped so the view clears until the refetch returns
	// fresh data. Without this guard the fallback would re-derive from that stale
	// `data` and show old content during the reset. Evaluated per-render (not in
	// the [data]-keyed memo) because `data` is unchanged at reset time.
	const canUseDerivedStreams = dataDerivedStreams !== null && skipInitialDataRef.current !== data;
	const effectiveFolderStream =
		folderStream.lastPageCount === -1 && canUseDerivedStreams
			? dataDerivedStreams!.folder
			: folderStream;
	const effectiveContentStream =
		contentStream.lastPageCount === -1 && canUseDerivedStreams
			? dataDerivedStreams!.content
			: contentStream;

	// --- Pagination query state ---
	const [paginateVariables, setPaginateVariables] = useState<{
		type: 'FOLDER' | 'CONTENT';
		variables: DualStreamQueriesPaginateQuery$variables;
	} | null>(null);

	const {
		data: paginateData,
		loading: paginateLoading,
		error: paginateError,
	} = useQueryInterop<
		DualStreamQueriesPaginateQuery$data,
		DualStreamQueriesPaginateQuery$variables
	>(FolderOverviewDualStreamPaginateQuery, {
		variables: paginateVariables?.variables ?? {
			cloudId: cloudId || '',
			parentId: contentId,
			spaceKey,
			type: 'FOLDER' as const,
			first: FETCH_SIZE,
		},
		skip: paginateVariables === null,
		fetchPolicy: 'network-only',
	});

	// --- Merge pagination response into the correct stream ---
	const lastPaginateDataRef = useRef<unknown>(null);

	useEffect(() => {
		if (!paginateVariables) {
			return;
		}
		// Handle pagination errors — mark the failing stream as exhausted so
		// pagination stops gracefully.  The caller can log paginateError for
		// observability; existing items remain visible (no error UI).
		if (paginateError) {
			/* eslint-disable @atlassian/perf-linting/no-chain-state-updates -- React 18 batches inside useEffect */
			const setFailedStream =
				paginateVariables.type === 'FOLDER' ? setFolderStream : setContentStream;
			setFailedStream((prev) => ({ ...prev, exhausted: true, hasNextPage: false }));
			setLastPaginateError(paginateError);
			setIsFetchingMore(false);
			setPaginateVariables(null);
			/* eslint-enable @atlassian/perf-linting/no-chain-state-updates */
			return;
		}
		if (paginateLoading || !paginateData) {
			return;
		}
		if (lastPaginateDataRef.current === paginateData) {
			return;
		}
		lastPaginateDataRef.current = paginateData;

		const newNodes: ContentTreeNode[] = [];
		for (const n of paginateData.confluence_contentTreeChildren?.nodes ?? []) {
			if (n?.id) {
				newNodes.push(n as ContentTreeNode);
			}
		}
		const pageInfo = paginateData.confluence_contentTreeChildren?.pageInfo;
		const hasNextPage = pageInfo?.hasNextPage ?? false;
		const endCursor = pageInfo?.endCursor ?? null;
		const exhausted = newNodes.length === 0 || !hasNextPage;

		const setStream = paginateVariables.type === 'FOLDER' ? setFolderStream : setContentStream;

		setStream((prev) => ({
			nodes: [...prev.nodes, ...newNodes],
			cursor: endCursor,
			hasNextPage,
			lastPageCount: newNodes.length,
			exhausted,
		}));
		/* eslint-disable @atlassian/perf-linting/no-chain-state-updates -- React 18 batches inside useEffect */
		setIsFetchingMore(false);
		setPaginateVariables(null);
		/* eslint-enable @atlassian/perf-linting/no-chain-state-updates */
	}, [paginateData, paginateLoading, paginateError, paginateVariables]);

	// --- Compute visible nodes ---
	// Folders first, then content — but only show content when folders are exhausted.
	// Read from the effective streams so SSR / first client render derive directly
	// from `data` before the merge effect populates state.
	const foldersExhausted = effectiveFolderStream.exhausted;
	const allFolderNodesShown = visibleCount >= effectiveFolderStream.nodes.length;
	const showContent = foldersExhausted && allFolderNodesShown;

	const effectiveFolderNodes = effectiveFolderStream.nodes;
	const effectiveContentNodes = effectiveContentStream.nodes;

	const visibleNodes = useMemo(() => {
		if (!showContent) {
			// Only show folders, capped at visibleCount
			return effectiveFolderNodes.slice(0, visibleCount);
		}
		// All folders + content up to visibleCount
		const remainingSlots = visibleCount - effectiveFolderNodes.length;
		const visibleContent = remainingSlots > 0 ? effectiveContentNodes.slice(0, remainingSlots) : [];
		return [...effectiveFolderNodes, ...visibleContent];
	}, [effectiveFolderNodes, effectiveContentNodes, visibleCount, showContent]);

	// --- Load next page (called by IntersectionObserver) ---
	const loadNextPage = useCallback(() => {
		if (loading || isFetchingMore) {
			return;
		}

		// Nothing left to paginate — bail to avoid an infinite re-render loop.
		if (folderStream.exhausted && contentStream.exhausted) {
			return;
		}

		// Compute the next visible count *before* updating state so the refill
		// check uses the value that will actually be rendered next.
		const nextVisibleCount = visibleCountRef.current + PAGE_SIZE;
		setVisibleCount(nextVisibleCount);

		// Check if we need to refill a buffer
		const folderRemaining = folderStream.nodes.length - nextVisibleCount;
		const contentRemaining =
			contentStream.nodes.length - (nextVisibleCount - folderStream.nodes.length);

		if (!folderStream.exhausted && folderRemaining < REFILL_THRESHOLD) {
			// Refill folder buffer
			setPaginateVariables({
				type: 'FOLDER',
				variables: {
					cloudId: cloudId || '',
					parentId: contentId,
					spaceKey,
					type: 'FOLDER' as const,
					first: FETCH_SIZE,
					after: folderStream.cursor,
				},
			});
			setIsFetchingMore(true);
		} else if (
			folderStream.exhausted &&
			!contentStream.exhausted &&
			contentRemaining < REFILL_THRESHOLD
		) {
			// Refill content buffer
			setPaginateVariables({
				type: 'CONTENT',
				variables: {
					cloudId: cloudId || '',
					parentId: contentId,
					spaceKey,
					type: 'CONTENT' as const,
					first: FETCH_SIZE,
					after: contentStream.cursor,
				},
			});
			setIsFetchingMore(true);
		}
	}, [loading, isFetchingMore, folderStream, contentStream, cloudId, contentId, spaceKey]);

	// --- Reset pagination ---
	const resetPagination = useCallback(() => {
		lastPaginateDataRef.current = null;
		skipInitialDataRef.current = lastInitialDataRef.current;
		setFolderStream(EMPTY_STREAM);
		setContentStream(EMPTY_STREAM);
		setVisibleCount(PAGE_SIZE);
		setIsFetchingMore(false);
		setLastPaginateError(null);
		setPaginateVariables(null);
		void refetch({
			cloudId: cloudId || '',
			parentId: contentId,
			spaceKey,
			contentId,
			first: FETCH_SIZE,
			includeDevDocsSyncStatus,
		});
	}, [cloudId, contentId, includeDevDocsSyncStatus, spaceKey, refetch]);

	// --- Child IDs (for enrichment query and refetch subscription) ---
	const childIds = useMemo(() => {
		const ids: string[] = [];
		for (const n of visibleNodes) {
			ids.push(n.id);
		}
		return ids;
	}, [visibleNodes]);

	const childIdsSet = useMemo(() => new Set(childIds), [childIds]);

	return {
		visibleNodes,
		loading: loading && !data,
		isFetchingMore,
		ptpage: data?.ptpage ?? null,
		singleContent: data?.singleContent ?? null,
		error: error ?? null,
		paginateError: lastPaginateError,
		loadNextPage,
		resetPagination,
		childIds,
		childIdsSet,
	};
};
